import React, { useState, useEffect, useMemo } from 'react';
import { 
    Plus, 
    Edit, 
    Eye, 
    BookOpen, 
    Users, 
    Search, 
    X, 
    ChevronLeft, 
    ChevronRight, 
    School, 
    Clock, 
    Trash2, 
    Award, 
    Calendar, 
    MapPin, 
    Check,
    Save,
    GraduationCap
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
    Subject, 
    getSubjects, 
    getClasses, 
    addSubject, 
    addClass, 
    updateClass, 
    deleteClass, 
    addSubjectToClass, 
    removeSubjectFromClass,
    editSubjectInClass
} from '../utils/subjectsClasses';
import {
    Campus,
    Class as CampusClass,
    Badge,
    getCampuses,
    addCampus,
    updateCampus,
    deleteCampus,
    addClassWithCampus,
    updateClassWithCampus,
    deleteClassWithCampus,
    getBadges,
    addBadge,
    deleteBadge,
    updateBadge
} from '../utils/management';
import { getStudentsWithBadge } from '../utils/student';
import { DaySchedule, BreakTime } from '../types/schedule';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import useTenantLimits from '../hooks/useTenantLimits';

const AdminManagement: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const { isLimitReached, getLimit } = useTenantLimits();

    // Navigation Tabs
    const [activeTab, setActiveTab] = useState<'campus' | 'classes' | 'badges'>('campus');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Common Lists
    const [campuses, setCampuses] = useState<Campus[]>([]);
    const [classes, setClasses] = useState<CampusClass[]>([]);
    const [badges, setBadges] = useState<Badge[]>([]);

    // ----------------------------------------------------
    // Campus State & Handlers
    // ----------------------------------------------------
    const [showCampusModal, setShowCampusModal] = useState(false);
    const [editingCampus, setEditingCampus] = useState<Campus | null>(null);
    const [campusForm, setCampusForm] = useState({
        name: '',
        address: ''
    });

    // Campus School Timings Editor State
    const [showTimingModal, setShowTimingModal] = useState(false);
    const [timingCampus, setTimingCampus] = useState<Campus | null>(null);
    const [timingFormData, setTimingFormData] = useState({
        schoolStartTime: '08:00',
        schoolEndTime: '15:00',
        days: [] as DaySchedule[]
    });

    // ----------------------------------------------------
    // Class State & Handlers
    // ----------------------------------------------------
    const [showClassModal, setShowClassModal] = useState(false);
    const [editingClass, setEditingClass] = useState<CampusClass | null>(null);
    const [classForm, setClassForm] = useState({
        grade: '',
        section: '',
        campusId: '',
        subjects: [] as string[]
    });
    const [savingClass, setSavingClass] = useState(false);
    const [savingCampus, setSavingCampus] = useState(false);
    const [viewingClass, setViewingClass] = useState<CampusClass | null>(null);

    // Subject Management state within Class Details
    const [showSubjectForm, setShowSubjectForm] = useState(false);
    const [selectedClass, setSelectedClass] = useState<CampusClass | null>(null);
    const [subjectForm, setSubjectForm] = useState({ name: '' });

    // ----------------------------------------------------
    // Badge State & Handlers
    // ----------------------------------------------------
    const [showBadgeModal, setShowBadgeModal] = useState(false);
    const [badgeForm, setBadgeForm] = useState({
        campusId: '',
        badgeNumber: 1,
        title: '',
        year: new Date().getFullYear()
    });
    const [savingBadge, setSavingBadge] = useState(false);
    const [editingBadge, setEditingBadge] = useState<Badge | null>(null);
    const [students, setStudents] = useState<any[]>([]);
    const [editingSubject, setEditingSubject] = useState<{ classId: string, oldName: string } | null>(null);
    const [viewingBadge, setViewingBadge] = useState<Badge | null>(null);

    // ----------------------------------------------------
    // Fetch Data
    // ----------------------------------------------------
    const fetchData = async () => {
        setLoading(true);
        try {
            const [campusesData, classesData, badgesData, studentsSnap] = await Promise.all([
                getCampuses(),
                getClasses(),
                getBadges(),
                getDocs(collection(db, 'students'))
            ]);
            setCampuses(campusesData);
            setClasses(classesData as CampusClass[]);
            setBadges(badgesData);
            setStudents(studentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error('Error fetching management data:', error);
            showError('Error', 'Failed to fetch management details');
        } finally {
            setLoading(false);
        }
    };

    const getStudentCountForClass = (grade: string, section: string) => {
        return students.filter(s => s.academicInfo?.currentGrade === grade && s.academicInfo?.section === section).length;
    };

    const getStudentCountForBadge = (badgeTitle: string) => {
        return students.filter(s => s.academicInfo?.badge === badgeTitle).length;
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Reset pagination when searching or switching tabs
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeTab]);

    // Inline Campus Editor State & Date Implementation Modal States
    const [editedCampuses, setEditedCampuses] = useState<Record<string, { schoolStartTime: string; schoolEndTime: string; days: DaySchedule[] }>>({});
    const [showImplementationModal, setShowImplementationModal] = useState(false);
    const [selectedCampusForImplementation, setSelectedCampusForImplementation] = useState<Campus | null>(null);
    const [implementationTimeOption, setImplementationTimeOption] = useState<'immediate' | 'next-monday' | 'custom'>('immediate');
    const [customImplementationDate, setCustomImplementationDate] = useState(new Date().toISOString().substring(0, 10));
    const [savingCampusTiming, setSavingCampusTiming] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{
        type: 'campus' | 'class' | 'subject' | 'badge';
        id: string;
        title: string;
        message: string;
        extraData?: any;
    } | null>(null);

    // Sync editedCampuses whenever campuses load
    useEffect(() => {
        setEditedCampuses(prev => {
            const next = { ...prev };
            campuses.forEach(c => {
                if (!next[c.id]) {
                    const hasConfig = c.schoolStartTime && c.schoolEndTime && c.days && c.days.length > 0;
                    next[c.id] = {
                        schoolStartTime: c.schoolStartTime || '08:00 AM',
                        schoolEndTime: c.schoolEndTime || '03:00 PM',
                        days: hasConfig ? c.days! : [
                            { day: 'Monday', isActive: false, startTime: '08:00 AM', endTime: '03:00 PM', breakTimes: [] },
                            { day: 'Tuesday', isActive: false, startTime: '08:00 AM', endTime: '03:00 PM', breakTimes: [] },
                            { day: 'Wednesday', isActive: false, startTime: '08:00 AM', endTime: '03:00 PM', breakTimes: [] },
                            { day: 'Thursday', isActive: false, startTime: '08:00 AM', endTime: '03:00 PM', breakTimes: [] },
                            { day: 'Friday', isActive: false, startTime: '08:00 AM', endTime: '12:00 PM', breakTimes: [] },
                            { day: 'Saturday', isActive: false, startTime: '08:00 AM', endTime: '03:00 PM', breakTimes: [] },
                            { day: 'Sunday', isActive: false, startTime: '08:00 AM', endTime: '03:00 PM', breakTimes: [] }
                        ]
                    };
                }
            });
            return next;
        });
    }, [campuses]);

    // Helpers to convert 12-hour AM/PM string to 24-hour HH:MM and vice versa
    const convertTo24h = (time12h: string): string => {
        if (!time12h) return '08:00';
        if (/^\d{2}:\d{2}$/.test(time12h)) return time12h;
        
        const parts = time12h.split(' ');
        const timePart = parts[0];
        const modifier = parts[1];
        
        if (!timePart) return '08:00';
        let [hoursStr, minutes] = timePart.split(':');
        let hours = parseInt(hoursStr, 10);
        
        if (hours === 12) {
            hours = 0;
        }
        if (modifier === 'PM' || modifier === 'pm') {
            hours += 12;
        }
        return `${String(hours).padStart(2, '0')}:${minutes || '00'}`;
    };

    const convertTo12h = (time24h: string): string => {
        if (!time24h) return '08:00 AM';
        if (/^\d{2}:\d{2}\s?(AM|PM|am|pm)$/.test(time24h)) return time24h;
        
        const parts = time24h.split(':');
        let hours = parseInt(parts[0], 10);
        const minutes = parts[1] || '00';
        
        const modifier = hours >= 12 ? 'PM' : 'AM';
        if (hours > 12) {
            hours -= 12;
        }
        if (hours === 0) {
            hours = 12;
        }
        return `${String(hours).padStart(2, '0')}:${minutes} ${modifier}`;
    };

    const getNextMondayStr = (): string => {
        const d = new Date();
        const day = d.getDay();
        const diff = d.getDate() + (day === 0 ? 1 : 8 - day);
        const nextMonday = new Date(d.setDate(diff));
        return nextMonday.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getOverallRange = (days: DaySchedule[]) => {
        const active = days.filter(d => d.isActive);
        if (active.length === 0) return 'No School Time Configured';
        
        const startTimes = active.map(d => convertTo24h(d.startTime));
        const endTimes = active.map(d => convertTo24h(d.endTime));
        
        startTimes.sort();
        endTimes.sort();
        
        const minStart = convertTo12h(startTimes[0]);
        const maxEnd = convertTo12h(endTimes[endTimes.length - 1]);
        
        return `${minStart} - ${maxEnd}`;
    };

    const isCampusModified = (campus: Campus): boolean => {
        const edits = editedCampuses[campus.id];
        if (!edits) return false;
        
        const hasOriginalConfig = campus.schoolStartTime && campus.schoolEndTime && campus.days && campus.days.length > 0;
        if (!hasOriginalConfig) {
            return edits.days.some(d => d.isActive);
        }
        
        const origDays = campus.days || [];
        for (const d of edits.days) {
            const origD = origDays.find(od => od.day === d.day);
            if (!origD) return true;
            if (origD.isActive !== d.isActive) return true;
            if (d.isActive) {
                if (origD.startTime !== d.startTime) return true;
                if (origD.endTime !== d.endTime) return true;
            }
        }
        return false;
    };

    // Toggle active days and change times inline
    const handleToggleDay = (campusId: string, dayName: string) => {
        setEditedCampuses(prev => {
            const next = { ...prev };
            if (!next[campusId]) return prev;
            
            const updatedDays = next[campusId].days.map(d => {
                if (d.day === dayName) {
                    return { ...d, isActive: !d.isActive };
                }
                return d;
            });
            
            return {
                ...next,
                [campusId]: {
                    ...next[campusId],
                    days: updatedDays
                }
            };
        });
    };

    const handleDayTimeChange = (campusId: string, dayName: string, field: 'startTime' | 'endTime', value: string) => {
        setEditedCampuses(prev => {
            const next = { ...prev };
            if (!next[campusId]) return prev;
            
            const updatedDays = next[campusId].days.map(d => {
                if (d.day === dayName) {
                    return { ...d, [field]: value };
                }
                return d;
            });
            
            return {
                ...next,
                [campusId]: {
                    ...next[campusId],
                    days: updatedDays
                }
            };
        });
    };

    // Trigger saving & ask for implementation date
    const handleTriggerImplementation = (campus: Campus) => {
        setSelectedCampusForImplementation(campus);
        setImplementationTimeOption('immediate');
        setShowImplementationModal(true);
    };

    const handleConfirmImplementation = async () => {
        if (!selectedCampusForImplementation) return;
        
        const edits = editedCampuses[selectedCampusForImplementation.id];
        if (!edits) return;
        
        const activeDays = edits.days.filter(d => d.isActive);
        if (activeDays.length === 0) {
            showError('Validation Error', 'Please enable at least one active day.');
            return;
        }
        
        setSavingCampusTiming(true);
        try {
            const rangeStr = getOverallRange(edits.days);
            const [schoolStartTime, schoolEndTime] = rangeStr.split(' - ');
            
            let implementationMessage = '';
            if (implementationTimeOption === 'immediate') {
                implementationMessage = 'immediately';
            } else if (implementationTimeOption === 'next-monday') {
                implementationMessage = `starting next Monday (${getNextMondayStr()})`;
            } else {
                implementationMessage = `on ${new Date(customImplementationDate).toLocaleDateString()}`;
            }

            await updateCampus(selectedCampusForImplementation.id, {
                schoolStartTime: schoolStartTime || '08:00 AM',
                schoolEndTime: schoolEndTime || '03:00 PM',
                days: edits.days
            });
            
            showSuccess('Timing Saved', `Timings for ${selectedCampusForImplementation.name} saved, set to implement ${implementationMessage}!`);
            
            // Reload campuses
            const campusesData = await getCampuses();
            setCampuses(campusesData);
            
            setShowImplementationModal(false);
            setSelectedCampusForImplementation(null);
        } catch (err) {
            console.error('Failed to save timings:', err);
            showError('Error', 'Failed to update campus timings.');
        } finally {
            setSavingCampusTiming(false);
        }
    };

    // ----------------------------------------------------
    // Campus Handlers
    // ----------------------------------------------------
    const handleCampusSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingCampus(true);
        try {
            if (editingCampus) {
                await updateCampus(editingCampus.id, campusForm);
                showSuccess('Success', 'Campus updated successfully');
            } else {
                await addCampus(campusForm);
                showSuccess('Success', 'Campus created successfully (configure times next!)');
            }
            setShowCampusModal(false);
            setEditingCampus(null);
            setCampusForm({ name: '', address: '' });
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to save campus');
        } finally {
            setSavingCampus(false);
        }
    };

    const handleEditCampus = (campus: Campus) => {
        setEditingCampus(campus);
        setCampusForm({
            name: campus.name,
            address: campus.address
        });
        setShowCampusModal(true);
    };

    const handleDeleteCampusClick = (id: string) => {
        setDeleteTarget({
            type: 'campus',
            id,
            title: 'Delete Campus Branch',
            message: 'Are you sure you want to delete this campus branch? This will affect all classes and badges designated to it.'
        });
    };

    // Timing Configuration Handlers
    const handleConfigureTiming = (campus: Campus) => {
        setTimingCampus(campus);
        
        const defaultDays: DaySchedule[] = [
            { day: 'Monday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Tuesday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Wednesday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Thursday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Friday', isActive: true, startTime: '08:00', endTime: '12:00', breakTimes: [] },
            { day: 'Saturday', isActive: false, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Sunday', isActive: false, startTime: '08:00', endTime: '15:00', breakTimes: [] }
        ];

        setTimingFormData({
            schoolStartTime: campus.schoolStartTime || '08:00',
            schoolEndTime: campus.schoolEndTime || '15:00',
            days: campus.days && campus.days.length > 0 ? campus.days.map(d => ({
                ...d,
                breakTimes: d.breakTimes || []
            })) : defaultDays
        });
        setShowTimingModal(true);
    };

    const handleTimingSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!timingCampus) return;

        const activeDays = timingFormData.days.filter(d => d.isActive);
        if (activeDays.length === 0) {
            showError('Validation Error', 'Please enable at least one school day');
            return;
        }

        try {
            await updateCampus(timingCampus.id, {
                schoolStartTime: timingFormData.schoolStartTime,
                schoolEndTime: timingFormData.schoolEndTime,
                days: timingFormData.days
            });
            showSuccess('Timing Configured', 'Campus timings and days schedule updated successfully');
            setShowTimingModal(false);
            setTimingCampus(null);
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to save campus school timings');
        }
    };

    const toggleDay = (dayIndex: number) => {
        setTimingFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? { ...day, isActive: !day.isActive } : day
            )
        }));
    };

    const updateDayTime = (dayIndex: number, field: 'startTime' | 'endTime', value: string) => {
        setTimingFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? { ...day, [field]: value } : day
            )
        }));
    };

    const addBreakTime = (dayIndex: number) => {
        const newBreak: BreakTime = {
            id: Date.now().toString(),
            name: 'Break',
            startTime: '10:00',
            endTime: '10:15',
            duration: 15
        };
        setTimingFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? { ...day, breakTimes: [...(day.breakTimes || []), newBreak] } : day
            )
        }));
    };

    const updateBreakTime = (dayIndex: number, breakIndex: number, field: keyof BreakTime, value: string | number) => {
        setTimingFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? {
                    ...day,
                    breakTimes: (day.breakTimes || []).map((breakTime, j) =>
                        j === breakIndex ? { ...breakTime, [field]: value } : breakTime
                    )
                } : day
            )
        }));
    };

    const removeBreakTime = (dayIndex: number, breakIndex: number) => {
        setTimingFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? {
                    ...day,
                    breakTimes: (day.breakTimes || []).filter((_, j) => j !== breakIndex)
                } : day
            )
        }));
    };

    const handleBreakTimeChange = (dayIndex: number, breakIndex: number, field: 'startTime' | 'endTime', value: string) => {
        updateBreakTime(dayIndex, breakIndex, field, value);
        
        const breakTime = timingFormData.days[dayIndex].breakTimes[breakIndex];
        const newBreakTime = { ...breakTime, [field]: value };
        
        const [startHour, startMin] = newBreakTime.startTime.split(':').map(Number);
        const [endHour, endMin] = newBreakTime.endTime.split(':').map(Number);
        const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);

        updateBreakTime(dayIndex, breakIndex, 'duration', duration > 0 ? duration : 0);
    };

    // ----------------------------------------------------
    // Class Handlers
    // ----------------------------------------------------
    const handleClassSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!classForm.campusId) {
            showError('Validation Error', 'Please select a Campus for this class');
            return;
        }

        setSavingClass(true);
        try {
            if (editingClass) {
                await updateClassWithCampus(editingClass.id, {
                    grade: classForm.grade,
                    section: classForm.section,
                    campusId: classForm.campusId
                });
                showSuccess('Success', 'Class updated successfully');
            } else {
                await addClassWithCampus({
                    grade: classForm.grade,
                    section: classForm.section,
                    campusId: classForm.campusId
                });
                showSuccess('Success', 'Class added successfully');
            }
            setShowClassModal(false);
            setEditingClass(null);
            setClassForm({ grade: '', section: '', campusId: '', subjects: [] });
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to save class');
        } finally {
            setSavingClass(false);
        }
    };

    const handleEditClass = (cls: CampusClass) => {
        setEditingClass(cls);
        setClassForm({
            grade: cls.grade,
            section: cls.section,
            campusId: cls.campusId || '',
            subjects: cls.subjects || []
        });
        setShowClassModal(true);
    };

    const handleDeleteClassClick = (id: string) => {
        setDeleteTarget({
            type: 'class',
            id,
            title: 'Delete Class',
            message: 'Are you sure you want to delete this class?'
        });
    };

    // Subject Management within a Class Detail Modal
    const handleAddSubjectToClassClick = (cls: CampusClass) => {
        setSelectedClass(cls);
        setEditingSubject(null);
        setSubjectForm({ name: '' });
        setShowSubjectForm(true);
    };

    const handleEditSubjectClick = (cls: CampusClass, subjectName: string) => {
        setSelectedClass(cls);
        setEditingSubject({ classId: cls.id, oldName: subjectName });
        setSubjectForm({ name: subjectName });
        setShowSubjectForm(true);
    };

    const handleSubjectSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedClass) return;

        try {
            if (editingSubject) {
                await editSubjectInClass(selectedClass.id, editingSubject.oldName, subjectForm.name);
                showSuccess('Subject Updated', 'Subject updated successfully');
                setEditingSubject(null);
            } else {
                await addSubjectToClass(selectedClass.id, subjectForm.name);
                showSuccess('Subject Added', 'Subject added successfully to class');
            }
            setShowSubjectForm(false);
            setSubjectForm({ name: '' });
            
            // Refresh viewing detail model reference
            const updatedClasses = await getClasses();
            setClasses(updatedClasses as CampusClass[]);
            const newClassRef = updatedClasses.find(c => c.id === selectedClass.id);
            if (newClassRef) setViewingClass(newClassRef as CampusClass);
        } catch (error) {
            showError('Error', editingSubject ? 'Failed to update subject' : 'Failed to add subject');
        }
    };

    const handleRemoveSubject = (cls: CampusClass, subjectName: string) => {
        setDeleteTarget({
            type: 'subject',
            id: cls.id,
            title: 'Remove Subject',
            message: `Are you sure you want to remove ${subjectName} from this class?`,
            extraData: { cls, subjectName }
        });
    };

    // ----------------------------------------------------
    // Badge Handlers
    // ----------------------------------------------------
    const handleBadgeCampusChange = (campusId: string) => {
        // Auto increase number of the selected campus start from 1
        const existingBadgesForCampus = badges.filter(b => b.campusId === campusId);
        const nextBadgeNumber = existingBadgesForCampus.length + 1;

        setBadgeForm(prev => ({
            ...prev,
            campusId,
            badgeNumber: nextBadgeNumber
        }));
    };

    const handleBadgeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!badgeForm.campusId) {
            showError('Validation Error', 'Please select a Campus first');
            return;
        }

        setSavingBadge(true);
        try {
            if (editingBadge) {
                await updateBadge(editingBadge.id, {
                    campusId: badgeForm.campusId,
                    badgeNumber: badgeForm.badgeNumber,
                    title: badgeForm.title,
                    year: Number(badgeForm.year)
                });
                showSuccess('Success', 'Badge updated successfully');
            } else {
                await addBadge({
                    campusId: badgeForm.campusId,
                    badgeNumber: badgeForm.badgeNumber,
                    title: badgeForm.title,
                    year: Number(badgeForm.year)
                });
                showSuccess('Success', 'Badge created successfully');
            }
            setShowBadgeModal(false);
            setEditingBadge(null);
            setBadgeForm({
                campusId: '',
                badgeNumber: 1,
                title: '',
                year: new Date().getFullYear()
            });
            fetchData();
        } catch (error) {
            showError('Error', 'Failed to save badge');
        } finally {
            setSavingBadge(false);
        }
    };

    const handleEditBadge = (badge: Badge) => {
        setEditingBadge(badge);
        setBadgeForm({
            campusId: badge.campusId,
            badgeNumber: badge.badgeNumber,
            title: badge.title,
            year: badge.year
        });
        setShowBadgeModal(true);
    };

    const handleDeleteBadgeClick = (id: string) => {
        setDeleteTarget({
            type: 'badge',
            id,
            title: 'Delete Badge',
            message: 'Are you sure you want to delete this badge?'
        });
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        const { type, id, extraData } = deleteTarget;
        setDeleteTarget(null);

        try {
            if (type === 'campus') {
                await deleteCampus(id);
                showSuccess('Deleted', 'Campus deleted successfully');
            } else if (type === 'class') {
                await deleteClassWithCampus(id);
                showSuccess('Deleted', 'Class deleted successfully');
            } else if (type === 'badge') {
                const badgeToDelete = badges.find(b => b.id === id);
                if (badgeToDelete) {
                    const linkedStudents = await getStudentsWithBadge(badgeToDelete.title);
                    if (linkedStudents.length > 0) {
                        showError('Delete Failed', `${linkedStudents.length} student${linkedStudents.length > 1 ? 's are' : ' is'} in this badge. first change them`);
                        return;
                    }
                }
                await deleteBadge(id);
                showSuccess('Deleted', 'Badge deleted successfully');
            } else if (type === 'subject') {
                const { cls, subjectName } = extraData;
                await removeSubjectFromClass(cls.id, subjectName);
                showSuccess('Removed', 'Subject removed from class');
                
                // Refresh viewing Class Hub modal state
                const updatedClasses = await getClasses();
                setClasses(updatedClasses as CampusClass[]);
                const newClassRef = updatedClasses.find(c => c.id === cls.id);
                if (newClassRef) setViewingClass(newClassRef as CampusClass);
            }
            fetchData();
        } catch (error) {
            showError('Error', `Failed to delete ${type}`);
        }
    };

    // Get Campus Name Helper
    const getCampusName = (campusId: string) => {
        const found = campuses.find(c => c.id === campusId);
        return found ? found.name : 'Unknown Campus';
    };

    // ----------------------------------------------------
    // Filter and Pagination Computations
    // ----------------------------------------------------
    const filteredCampuses = useMemo(() => {
        return campuses.filter(c => 
            c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.address.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [campuses, searchTerm]);

    const filteredClasses = useMemo(() => {
        return classes.filter(cls => {
            const campusName = getCampusName(cls.campusId);
            return (
                cls.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
                cls.section.toLowerCase().includes(searchTerm.toLowerCase()) ||
                campusName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
    }, [classes, searchTerm, campuses]);

    const filteredBadges = useMemo(() => {
        return badges.filter(b => {
            const campusName = getCampusName(b.campusId);
            return (
                b.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                campusName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                String(b.badgeNumber).includes(searchTerm) ||
                String(b.year).includes(searchTerm)
            );
        });
    }, [badges, searchTerm, campuses]);

    // Active View Calculations based on Tab
    const currentItemsCount = {
        campus: filteredCampuses.length,
        classes: filteredClasses.length,
        badges: filteredBadges.length
    }[activeTab];

    const totalPages = Math.ceil(currentItemsCount / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;

    const paginatedCampuses = filteredCampuses.slice(startIndex, startIndex + itemsPerPage);
    const paginatedClasses = filteredClasses.slice(startIndex, startIndex + itemsPerPage);
    const paginatedBadges = filteredBadges.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <School className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Campus & Academic Management</h2>
                        <p className="text-gray-600">Configure campuses, class groups, and custom student badges</p>
                    </div>
                </div>
                
                {/* Actions based on Active Tab */}
                <div className="flex items-center space-x-3">
                    {activeTab === 'campus' && (
                        <Button
                            onClick={() => {
                                if (isLimitReached('Management', campuses.length)) {
                                    const lim = getLimit('Management');
                                    showError(
                                        'Campus Limit Reached',
                                        `You have reached the maximum limit of ${lim} campuses. Please contact Dynamix Zone to increase your Campus limit.`
                                    );
                                    return;
                                }
                                setEditingCampus(null);
                                setCampusForm({ name: '', address: '' });
                                setShowCampusModal(true);
                            }}
                            className="flex items-center space-x-2"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add Campus</span>
                        </Button>
                    )}
                    {activeTab === 'classes' && (
                        <Button
                            onClick={() => {
                                setEditingClass(null);
                                setClassForm({ grade: '', section: '', campusId: '', subjects: [] });
                                setShowClassModal(true);
                            }}
                            className="flex items-center space-x-2"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add Class</span>
                        </Button>
                    )}
                    {activeTab === 'badges' && (
                        <Button
                            onClick={() => {
                                setEditingBadge(null);
                                setBadgeForm({
                                    campusId: '',
                                    badgeNumber: 1,
                                    title: '',
                                    year: new Date().getFullYear()
                                });
                                setShowBadgeModal(true);
                            }}
                            className="flex items-center space-x-2"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add Badge</span>
                        </Button>
                    )}
                </div>
            </div>

            {/* Navigation Tabs System */}
            <div className="flex border-b border-gray-200">
                <button
                    onClick={() => { setActiveTab('campus'); setSearchTerm(''); }}
                    className={`flex items-center space-x-2 py-3 px-6 text-sm font-semibold border-b-2 transition-all duration-300 ${
                        activeTab === 'campus'
                            ? 'border-emerald-500 text-emerald-600 bg-emerald-50/30 rounded-t-xl'
                            : 'border-transparent text-gray-500 hover:text-emerald-500 hover:bg-gray-50/50'
                    }`}
                >
                    <School className="w-4 h-4" />
                    <span>Campus ({campuses.length})</span>
                </button>
                <button
                    onClick={() => { setActiveTab('classes'); setSearchTerm(''); }}
                    className={`flex items-center space-x-2 py-3 px-6 text-sm font-semibold border-b-2 transition-all duration-300 ${
                        activeTab === 'classes'
                            ? 'border-emerald-500 text-emerald-600 bg-emerald-50/30 rounded-t-xl'
                            : 'border-transparent text-gray-500 hover:text-emerald-500 hover:bg-gray-50/50'
                    }`}
                >
                    <Users className="w-4 h-4" />
                    <span>Classes ({classes.length})</span>
                </button>
                <button
                    onClick={() => { setActiveTab('badges'); setSearchTerm(''); }}
                    className={`flex items-center space-x-2 py-3 px-6 text-sm font-semibold border-b-2 transition-all duration-300 ${
                        activeTab === 'badges'
                            ? 'border-emerald-500 text-emerald-600 bg-emerald-50/30 rounded-t-xl'
                            : 'border-transparent text-gray-500 hover:text-emerald-500 hover:bg-gray-50/50'
                    }`}
                >
                    <Award className="w-4 h-4" />
                    <span>Badges ({badges.length})</span>
                </button>
            </div>


            {/* Results Count Info */}
            {activeTab !== 'campus' && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                        Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, currentItemsCount)} of {currentItemsCount} items
                    </p>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Items per page:</span>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                    </div>
                </div>
            )}

            {/* ==================================================== */}
            {/* TAB 1: CAMPUS RENDER */}
            {/* ==================================================== */}
            {activeTab === 'campus' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1400px]">
                    {paginatedCampuses.map((campus) => {
                        const edits = editedCampuses[campus.id] || {
                            schoolStartTime: campus.schoolStartTime || '08:00 AM',
                            schoolEndTime: campus.schoolEndTime || '03:00 PM',
                            days: campus.days || []
                        };

                        const rangeStr = getOverallRange(edits.days);
                        const isModified = isCampusModified(campus);

                        return (
                            <Card key={campus.id} glassy={true} className="islamic-pattern !rounded-2xl p-5 bg-white/80 border border-slate-100/85 shadow-sm flex flex-col justify-between">
                                <div>
                                    {/* Card Header */}
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center space-x-3.5">
                                            <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-500 shadow-sm border border-rose-100/50">
                                                <Clock className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="text-base font-bold text-slate-800 tracking-tight">{campus.name}</h4>
                                                <p className="text-xs text-slate-500 font-semibold mt-0.5">{rangeStr}</p>
                                                <p className="text-[11px] text-slate-400 font-medium mt-0.5">{campus.address}</p>
                                            </div>
                                        </div>

                                        <div className="flex space-x-1">
                                            <Button
                                                variant={isModified ? "primary" : "secondary"}
                                                size="sm"
                                                onClick={() => handleTriggerImplementation(campus)}
                                                className={`p-1 rounded-lg transition-all ${
                                                    isModified
                                                        ? '!bg-emerald-600 hover:!bg-emerald-700 text-white shadow-sm border border-emerald-600'
                                                        : 'bg-slate-50 hover:bg-slate-100 text-slate-400 border border-slate-200/50'
                                                }`}
                                                title="Done / Save Campus Timings"
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleEditCampus(campus)}
                                                className="p-1 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-500 transition-all border border-slate-200/50"
                                                title="Edit Campus Details"
                                            >
                                                <Edit className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleDeleteCampusClick(campus.id)}
                                                className="p-1 rounded-lg bg-slate-50 hover:bg-rose-50 text-rose-500 hover:text-rose-600 transition-all border border-rose-200/30"
                                                title="Delete Campus"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Active Days section */}
                                    <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2.5">Schedule Timing:</h5>
                                    
                                    <div className="space-y-1.5">
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((dayName) => {
                                            const daySched = edits.days.find(d => d.day === dayName) || {
                                                day: dayName,
                                                isActive: false,
                                                startTime: '08:00 AM',
                                                endTime: '03:00 PM',
                                                breakTimes: []
                                            };

                                            return (
                                                <div
                                                    key={dayName}
                                                    className={`flex items-center justify-between py-1.5 px-3 bg-slate-50/40 hover:bg-slate-50 border border-slate-100 rounded-xl transition-all ${
                                                        daySched.isActive
                                                            ? 'bg-white border-slate-200/60 shadow-sm'
                                                            : 'opacity-70'
                                                    }`}
                                                >
                                                    {/* Check circle & Day name */}
                                                    <div
                                                        onClick={() => handleToggleDay(campus.id, dayName)}
                                                        className="flex items-center space-x-2.5 cursor-pointer select-none"
                                                    >
                                                        {daySched.isActive ? (
                                                            <div className="w-4.5 h-4.5 rounded-md bg-emerald-500 flex items-center justify-center text-white border border-emerald-600 shadow-sm transition-all">
                                                                <Check className="w-3 h-3 stroke-[3px]" />
                                                            </div>
                                                        ) : (
                                                            <div className="w-4.5 h-4.5 rounded-md border border-slate-300 hover:border-emerald-400 transition-all bg-white" />
                                                        )}
                                                        <span className={`text-sm font-semibold transition-colors ${
                                                            daySched.isActive ? 'text-slate-800' : 'text-slate-400'
                                                        }`}>
                                                            {dayName}
                                                        </span>
                                                    </div>

                                                    {/* Compact Time Inputs / OFF State */}
                                                    <div>
                                                        {daySched.isActive ? (
                                                            <div className="flex items-center space-x-1.5">
                                                                <div className="relative flex items-center px-2 py-0.5 bg-white border border-slate-200 rounded-lg hover:border-slate-300 focus-within:ring-2 focus-within:ring-emerald-500/10 focus-within:border-emerald-500 transition-all">
                                                                    <input
                                                                        type="time"
                                                                        value={convertTo24h(daySched.startTime)}
                                                                        onChange={(e) => handleDayTimeChange(campus.id, dayName, 'startTime', convertTo12h(e.target.value))}
                                                                        className="bg-transparent text-xs font-bold text-slate-700 outline-none border-none cursor-pointer w-24 h-6 px-1"
                                                                    />
                                                                </div>

                                                                <span className="text-slate-400 text-xs px-0.5">-</span>

                                                                <div className="relative flex items-center px-2 py-0.5 bg-white border border-slate-200 rounded-lg hover:border-slate-300 focus-within:ring-2 focus-within:ring-emerald-500/10 focus-within:border-emerald-500 transition-all">
                                                                    <input
                                                                        type="time"
                                                                        value={convertTo24h(daySched.endTime)}
                                                                        onChange={(e) => handleDayTimeChange(campus.id, dayName, 'endTime', convertTo12h(e.target.value))}
                                                                        className="bg-transparent text-xs font-bold text-slate-700 outline-none border-none cursor-pointer w-24 h-6 px-1"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-1">
                                                                OFF
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* ==================================================== */}
            {/* TAB 2: CLASSES RENDER */}
            {/* ==================================================== */}
            {activeTab === 'classes' && (
                viewMode === 'table' ? (
                    <Card glassy={true} className="islamic-pattern overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class Grade</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campus Branch</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Subjects</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedClasses.map((cls) => (
                                        <tr key={cls.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold">
                                                        {cls.grade.charAt(0)}
                                                    </div>
                                                    <div className="text-sm font-semibold text-gray-900">Grade {cls.grade}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                Section {cls.section}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className="inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                                                    <School className="w-3 h-3 mr-1" />
                                                    {getCampusName(cls.campusId)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                                                    <BookOpen className="w-3 h-3 mr-1" />
                                                    {cls.subjects?.length || 0} Subjects
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                                                    <Users className="w-3 h-3 mr-1" />
                                                    {getStudentCountForClass(cls.grade, cls.section)} Students
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <Button variant="secondary" size="sm" onClick={() => setViewingClass(cls)}>
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="secondary" size="sm" onClick={() => handleEditClass(cls)}>
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="secondary" 
                                                        size="sm" 
                                                        onClick={() => handleDeleteClassClick(cls.id)}
                                                        className="text-red-500 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedClasses.map((cls) => (
                            <Card key={cls.id} glassy={true} className="islamic-pattern">
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center shadow-md text-white font-bold">
                                                {cls.grade}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-900">Grade {cls.grade} - {cls.section}</h4>
                                                <p className="text-xs text-gray-500 flex items-center mt-0.5">
                                                    <School className="w-3.5 h-3.5 mr-0.5" />
                                                    {getCampusName(cls.campusId)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex space-x-1">
                                            <Button variant="secondary" size="sm" onClick={() => setViewingClass(cls)}>
                                                <Eye className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button variant="secondary" size="sm" onClick={() => handleEditClass(cls)}>
                                                <Edit className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button 
                                                variant="secondary" 
                                                size="sm" 
                                                onClick={() => handleDeleteClassClick(cls.id)}
                                                className="text-red-500 hover:bg-red-50"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center text-xs text-gray-500 border-t border-slate-100 pt-3">
                                        <span>Students: <strong className="text-emerald-600">{getStudentCountForClass(cls.grade, cls.section)} Active</strong></span>
                                        <span className="flex items-center">
                                            <BookOpen className="w-3 h-3 mr-1" />
                                            {cls.subjects?.length || 0} Subjects
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )
            )}

            {/* ==================================================== */}
            {/* TAB 3: BADGES RENDER */}
            {/* ==================================================== */}
            {activeTab === 'badges' && (
                viewMode === 'table' ? (
                    <Card glassy={true} className="islamic-pattern overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge #</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge Title</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campus Branch</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedBadges.map((badge) => (
                                        <tr key={badge.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-emerald-600">
                                                #{badge.badgeNumber}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 flex items-center">
                                                <Award className="w-4 h-4 text-emerald-500 mr-2" />
                                                {badge.title}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className="inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                                                    <School className="w-3 h-3 mr-1" />
                                                    {getCampusName(badge.campusId)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                                                {badge.year}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                                                    <Users className="w-3 h-3 mr-1" />
                                                    {getStudentCountForBadge(badge.title)} Students
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <Button variant="secondary" size="sm" onClick={() => setViewingBadge(badge)}>
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="secondary" size="sm" onClick={() => handleEditBadge(badge)}>
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="secondary" 
                                                        size="sm" 
                                                        onClick={() => handleDeleteBadgeClick(badge.id)}
                                                        className="text-red-500 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedBadges.map((badge) => (
                            <Card key={badge.id} glassy={true} className="islamic-pattern">
                                <div className="p-6 relative overflow-hidden group">
                                    {/* Decorative subtle background icon */}
                                    <Award className="w-24 h-24 text-emerald-50 absolute -right-4 -bottom-4 opacity-75 group-hover:scale-110 transition-transform" />
                                    
                                    <div className="flex justify-between items-start mb-4 relative z-10">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold">
                                                #{badge.badgeNumber}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-900">{badge.title}</h4>
                                                <p className="text-xs text-gray-500 flex items-center mt-0.5">
                                                    <School className="w-3.5 h-3.5 mr-0.5" />
                                                    {getCampusName(badge.campusId)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex space-x-1 relative z-10">
                                            <Button variant="secondary" size="sm" onClick={() => setViewingBadge(badge)}>
                                                <Eye className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button variant="secondary" size="sm" onClick={() => handleEditBadge(badge)}>
                                                <Edit className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button 
                                                variant="secondary" 
                                                size="sm" 
                                                onClick={() => handleDeleteBadgeClick(badge.id)}
                                                className="text-red-500 hover:bg-red-50"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="flex items-center text-xs text-gray-500 border-t border-slate-100 pt-3 relative z-10 font-semibold">
                                        <Calendar className="w-3.5 h-3.5 text-gray-400 mr-1" />
                                        <span>Academic Year: {badge.year}</span>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )
            )}

            {/* Empty States */}
            {currentItemsCount === 0 && (
                <Card glassy={true} className="islamic-pattern">
                    <div className="text-center py-12">
                        <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No records found</h3>
                        <p className="text-gray-600">
                            {searchTerm
                                ? 'Try adjusting your search query.'
                                : `No ${activeTab} configurations have been created yet.`}
                        </p>
                    </div>
                </Card>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <Card glassy={true} className="islamic-pattern">
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                    <Button
                                        key={pageNum}
                                        variant={currentPage === pageNum ? "primary" : "secondary"}
                                        size="sm"
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </Button>
                                ))}

                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* ==================================================== */}
            {/* MODALS & FORMS */}
            {/* ==================================================== */}
            
            {/* Campus Add/Edit Modal */}
            {showCampusModal && (
                <Modal
                    isOpen={true}
                    onClose={() => !savingCampus && setShowCampusModal(false)}
                    title={editingCampus ? 'Edit Campus Details' : 'Add Campus Branch'}
                    size="md"
                    footer={
                        <>
                            <Button variant="secondary" type="button" onClick={() => setShowCampusModal(false)} disabled={savingCampus}>
                                Cancel
                            </Button>
                            <Button type="submit" form="campus-form" disabled={savingCampus} loading={savingCampus}>
                                {editingCampus ? 'Save Changes' : 'Create Branch'}
                            </Button>
                        </>
                    }
                >
                    <form id="campus-form" onSubmit={handleCampusSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Branch Name</label>
                            <input
                                type="text"
                                value={campusForm.name}
                                onChange={(e) => setCampusForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g. Gulshan Campus"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                required
                                disabled={savingCampus}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Address</label>
                            <input
                                type="text"
                                value={campusForm.address}
                                onChange={(e) => setCampusForm(prev => ({ ...prev, address: e.target.value }))}
                                placeholder="Enter street and city address"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                required
                                disabled={savingCampus}
                            />
                        </div>
                    </form>
                </Modal>
            )}

            {/* Campus timings configure form modal */}
            {showTimingModal && timingCampus && (
                <Modal
                    isOpen={true}
                    onClose={() => { setShowTimingModal(false); setTimingCampus(null); }}
                    title={`Configure School Timings - ${timingCampus.name}`}
                    size="lg"
                >
                    <form onSubmit={handleTimingSubmit} className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Default School Start Time</label>
                                <input
                                    type="time"
                                    value={timingFormData.schoolStartTime}
                                    onChange={(e) => setTimingFormData(prev => ({ ...prev, schoolStartTime: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Default School End Time</label>
                                <input
                                    type="time"
                                    value={timingFormData.schoolEndTime}
                                    onChange={(e) => setTimingFormData(prev => ({ ...prev, schoolEndTime: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold text-gray-900 mb-2">Days of Week Settings</h4>
                            <p className="text-xs text-gray-500 mb-4">Toggle active status per day, set customizable start/end timing, and assign breaks.</p>

                            <div className="space-y-4">
                                {timingFormData.days.map((day, dayIndex) => (
                                    <div key={day.day} className="p-4 border border-gray-200 rounded-xl bg-slate-50/20">
                                        <div className="flex items-center justify-between mb-3">
                                            <label className="flex items-center space-x-3 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={day.isActive}
                                                    onChange={() => toggleDay(dayIndex)}
                                                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                                                />
                                                <span className="font-semibold text-gray-900">{day.day}</span>
                                            </label>
                                            {day.isActive && (
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="time"
                                                        value={day.startTime}
                                                        onChange={(e) => updateDayTime(dayIndex, 'startTime', e.target.value)}
                                                        className="px-3 py-1 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                    <span className="text-gray-400">to</span>
                                                    <input
                                                        type="time"
                                                        value={day.endTime}
                                                        onChange={(e) => updateDayTime(dayIndex, 'endTime', e.target.value)}
                                                        className="px-3 py-1 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {day.isActive && (
                                            <div className="mt-3 pl-7 pt-3 border-t border-dashed border-gray-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-xs font-bold text-slate-500">Day Break Sessions</span>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => addBreakTime(dayIndex)}
                                                    >
                                                        <Plus className="h-3.5 w-3.5 mr-1" />
                                                        Add Break
                                                    </Button>
                                                </div>

                                                <div className="space-y-3">
                                                    {(day.breakTimes || []).map((breakTime, breakIndex) => (
                                                        <div key={breakTime.id} className="p-3 border border-gray-200 rounded-xl bg-white flex flex-col md:flex-row items-center gap-3">
                                                            <input
                                                                type="text"
                                                                value={breakTime.name}
                                                                onChange={(e) => updateBreakTime(dayIndex, breakIndex, 'name', e.target.value)}
                                                                className="w-full md:w-1/3 px-3 py-1 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                                placeholder="e.g. Lunch"
                                                                required
                                                            />
                                                            <div className="flex items-center space-x-1.5 w-full md:w-auto">
                                                                <input
                                                                    type="time"
                                                                    value={breakTime.startTime}
                                                                    onChange={(e) => handleBreakTimeChange(dayIndex, breakIndex, 'startTime', e.target.value)}
                                                                    className="px-2 py-1 border border-gray-300 rounded-xl text-sm focus:outline-none"
                                                                    required
                                                                />
                                                                <span className="text-gray-400">-</span>
                                                                <input
                                                                    type="time"
                                                                    value={breakTime.endTime}
                                                                    onChange={(e) => handleBreakTimeChange(dayIndex, breakIndex, 'endTime', e.target.value)}
                                                                    className="px-2 py-1 border border-gray-300 rounded-xl text-sm focus:outline-none"
                                                                    required
                                                                />
                                                            </div>
                                                            <span className="text-xs font-semibold text-gray-500">({breakTime.duration}m)</span>
                                                            <Button
                                                                type="button"
                                                                variant="secondary"
                                                                size="sm"
                                                                onClick={() => removeBreakTime(dayIndex, breakIndex)}
                                                                className="text-red-500 hover:bg-red-50 ml-auto"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <Button variant="secondary" type="button" onClick={() => { setShowTimingModal(false); setTimingCampus(null); }}>
                                Cancel
                            </Button>
                            <Button type="submit">
                                <Save className="w-4 h-4 mr-2" />
                                Save Timings
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Class Add/Edit Modal */}
            {showClassModal && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowClassModal(false)}
                    title={editingClass ? 'Edit Class Details' : 'Add Class'}
                    size="md"
                    footer={
                        <>
                            <Button variant="secondary" type="button" onClick={() => setShowClassModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" form="class-form" disabled={savingClass}>
                                {savingClass ? 'Saving...' : editingClass ? 'Save Changes' : 'Create Class'}
                            </Button>
                        </>
                    }
                >
                    <form id="class-form" onSubmit={handleClassSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Grade / Year</label>
                                <input
                                    type="text"
                                    value={classForm.grade}
                                    onChange={(e) => setClassForm(prev => ({ ...prev, grade: e.target.value }))}
                                    placeholder="e.g. 10"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Section Name</label>
                                <input
                                    type="text"
                                    value={classForm.section}
                                    onChange={(e) => setClassForm(prev => ({ ...prev, section: e.target.value }))}
                                    placeholder="e.g. A"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Campus Branch</label>
                            <select
                                value={classForm.campusId}
                                onChange={(e) => setClassForm(prev => ({ ...prev, campusId: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-semibold text-gray-700 bg-white"
                                required
                            >
                                <option value="">-- Choose Campus --</option>
                                {campuses.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-gray-500 mt-1">Every class is strictly designated to a specific campus branch.</p>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Badge Add/Edit Modal */}
            {showBadgeModal && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowBadgeModal(false)}
                    title={editingBadge ? "Edit Student Academic Badge" : "Add Student Academic Badge"}
                    size="md"
                    footer={
                        <>
                            <Button variant="secondary" type="button" onClick={() => setShowBadgeModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" form="badge-form" disabled={savingBadge}>
                                {savingBadge ? (editingBadge ? 'Saving...' : 'Creating...') : (editingBadge ? 'Save Changes' : 'Create Badge')}
                            </Button>
                        </>
                    }
                >
                    <form id="badge-form" onSubmit={handleBadgeSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Campus Branch</label>
                            <select
                                value={badgeForm.campusId}
                                onChange={(e) => handleBadgeCampusChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-semibold text-gray-700 bg-white"
                                required
                            >
                                <option value="">-- Select Campus --</option>
                                {campuses.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Badge # (Auto-calculated)</label>
                            <input
                                type="number"
                                value={badgeForm.badgeNumber}
                                disabled
                                className="w-full px-3 py-2 border border-gray-200 bg-slate-50 text-emerald-600 font-bold rounded-xl cursor-not-allowed"
                            />
                            <p className="text-[10px] text-emerald-600 mt-1">✓ Auto-increments starting from 1 for the selected campus branch.</p>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Badge Title</label>
                            <input
                                type="text"
                                value={badgeForm.title}
                                onChange={(e) => setBadgeForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g. Star Student, Best Reader"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Academic Year</label>
                            <input
                                type="number"
                                value={badgeForm.year}
                                onChange={(e) => setBadgeForm(prev => ({ ...prev, year: Number(e.target.value) }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-semibold text-gray-700 bg-white"
                                required
                            />
                        </div>
                    </form>
                </Modal>
            )}            {/* Class Details & Subjects Modal */}
            {viewingClass && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingClass(null)}
                    title={`Class Hub: Grade ${viewingClass.grade} - Section ${viewingClass.section}`}
                    size="xl"
                    footer={
                        <>
                            <Button variant="secondary" onClick={() => setViewingClass(null)}>
                                Close
                            </Button>
                            <Button onClick={() => { setViewingClass(null); handleEditClass(viewingClass!); }}>
                                Edit Class
                            </Button>
                        </>
                    }
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Column 1: Settings and Subjects */}
                        <div className="space-y-6">
                            <Card className="p-5 border border-slate-100 bg-white/50 backdrop-blur-sm">
                                <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
                                    <School className="w-4 h-4 text-emerald-600 mr-1.5" />
                                    Class Settings
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">Grade Level:</span>
                                        <p className="font-semibold text-gray-900">Grade {viewingClass.grade}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Section Group:</span>
                                        <p className="font-semibold text-gray-900">Section {viewingClass.section}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <span className="text-gray-500">Campus Branch:</span>
                                        <p className="font-semibold text-emerald-600 flex items-center mt-0.5">
                                            <School className="w-4.5 h-4.5 mr-1" />
                                            {getCampusName(viewingClass.campusId)}
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            {/* Subject Lists */}
                            <Card className="p-5 border border-slate-100 bg-white/50 backdrop-blur-sm">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center">
                                        <BookOpen className="w-4 h-4 text-emerald-600 mr-1.5" />
                                        Assigned Academic Subjects ({viewingClass.subjects?.length || 0})
                                    </h4>
                                    <Button size="sm" variant="secondary" onClick={() => handleAddSubjectToClassClick(viewingClass)}>
                                        <Plus className="w-3.5 h-3.5 mr-1" />
                                        Add Subject
                                    </Button>
                                </div>

                                {viewingClass.subjects && viewingClass.subjects.length > 0 ? (
                                    <div className="overflow-x-auto max-h-[30vh] overflow-y-auto custom-scrollbar border border-slate-100 rounded-xl">
                                        <table className="min-w-full divide-y divide-gray-150 text-xs">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Subject Name</th>
                                                    <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase tracking-wider">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-slate-100">
                                                {viewingClass.subjects.map((subject, idx) => (
                                                    <tr key={idx} className="hover:bg-slate-50/55 transition-colors">
                                                        <td className="px-4 py-3 font-semibold text-slate-800">{subject}</td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="flex justify-end space-x-1">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleEditSubjectClick(viewingClass, subject)}
                                                                    className="text-emerald-600 p-1 hover:bg-emerald-50 rounded-lg transition-all"
                                                                    title="Edit Subject"
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveSubject(viewingClass, subject)}
                                                                    className="text-red-500 p-1 hover:bg-rose-50 hover:text-red-600 rounded-lg transition-all"
                                                                    title="Remove Subject"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-gray-500 text-xs">
                                        No subjects currently assigned to this class.
                                    </div>
                                )}
                            </Card>
                        </div>

                        {/* Column 2: Enrolled Students */}
                        <div>
                            {(() => {
                                const classStudents = students.filter(
                                    s => s.academicInfo?.currentGrade === viewingClass.grade && 
                                         s.academicInfo?.section === viewingClass.section
                                );
                                return (
                                    <Card className="p-5 border border-slate-100 h-full bg-white/50 backdrop-blur-sm flex flex-col justify-between">
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
                                                <Users className="w-4 h-4 text-emerald-600 mr-1.5" />
                                                Enrolled Students ({classStudents.length})
                                            </h4>

                                            {classStudents.length > 0 ? (
                                                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto custom-scrollbar border border-slate-100 rounded-xl">
                                                    <table className="min-w-full divide-y divide-gray-150 text-xs">
                                                        <thead className="bg-slate-50">
                                                            <tr>
                                                                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Student ID</th>
                                                                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Name</th>
                                                                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Roll No</th>
                                                                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-slate-100">
                                                            {classStudents.map((st) => (
                                                                <tr key={st.id} className="hover:bg-slate-50/55 transition-colors">
                                                                    <td className="px-4 py-3 font-mono font-bold text-slate-600">{st.studentId}</td>
                                                                    <td className="px-4 py-3 font-semibold text-slate-800">{st.firstName} {st.lastName}</td>
                                                                    <td className="px-4 py-3 text-slate-700">{st.academicInfo?.rollNumber || '—'}</td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-[10px] font-bold capitalize ${
                                                                            st.academicInfo?.status === 'active'
                                                                                ? 'bg-green-50 text-green-700'
                                                                                : 'bg-yellow-50 text-yellow-700'
                                                                        }`}>
                                                                            {st.academicInfo?.status || 'active'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-12 text-gray-500 text-xs">
                                                    <GraduationCap className="h-10 w-10 mx-auto mb-3 text-slate-400 opacity-60" />
                                                    No students currently enrolled in this class group.
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })()}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Badge Details Modal */}
            {viewingBadge && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingBadge(null)}
                    title={`Badge Detail: ${viewingBadge.title}`}
                    size="lg"
                    footer={
                        <Button variant="secondary" onClick={() => setViewingBadge(null)}>
                            Close
                        </Button>
                    }
                >
                    <div className="space-y-6">
                        <Card className="p-5 border border-slate-100 bg-white/50 backdrop-blur-sm">
                            <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
                                <Award className="w-4 h-4 text-emerald-600 mr-1.5" />
                                Badge Settings
                            </h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Badge Title:</span>
                                    <p className="font-semibold text-gray-900">{viewingBadge.title}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Badge Number:</span>
                                    <p className="font-semibold text-gray-900">#{viewingBadge.badgeNumber}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Academic Year:</span>
                                    <p className="font-semibold text-gray-900">{viewingBadge.year}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Campus Branch:</span>
                                    <p className="font-semibold text-emerald-600 flex items-center mt-0.5">
                                        <School className="w-4.5 h-4.5 mr-1" />
                                        {getCampusName(viewingBadge.campusId)}
                                    </p>
                                </div>
                            </div>
                        </Card>

                        {/* List of Enrolled Students with this Badge */}
                        {(() => {
                            const badgeStudents = students.filter(s => s.academicInfo?.badge === viewingBadge.title);
                            return (
                                <Card className="p-5 border border-slate-100 bg-white/50 backdrop-blur-sm flex flex-col justify-between">
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
                                            <Users className="w-4 h-4 text-emerald-600 mr-1.5" />
                                            Enrolled Students ({badgeStudents.length})
                                        </h4>

                                        {badgeStudents.length > 0 ? (
                                            <div className="overflow-x-auto max-h-[45vh] overflow-y-auto custom-scrollbar border border-slate-100 rounded-xl">
                                                <table className="min-w-full divide-y divide-gray-150 text-xs">
                                                    <thead className="bg-slate-50">
                                                        <tr>
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Student ID</th>
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Name</th>
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Grade & Section</th>
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-slate-100">
                                                        {badgeStudents.map((st) => (
                                                            <tr key={st.id} className="hover:bg-slate-50/55 transition-colors">
                                                                <td className="px-4 py-3 font-mono font-bold text-slate-600">{st.studentId}</td>
                                                                <td className="px-4 py-3 font-semibold text-slate-800">{st.firstName} {st.lastName}</td>
                                                                <td className="px-4 py-3 text-slate-700">Grade {st.academicInfo?.currentGrade} - {st.academicInfo?.section}</td>
                                                                <td className="px-4 py-3">
                                                                    <span className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-[10px] font-bold capitalize ${
                                                                        st.academicInfo?.status === 'active'
                                                                            ? 'bg-green-50 text-green-700'
                                                                            : 'bg-yellow-50 text-yellow-700'
                                                                    }`}>
                                                                        {st.academicInfo?.status || 'active'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-12 text-gray-500 text-xs">
                                                <GraduationCap className="h-10 w-10 mx-auto mb-3 text-slate-400 opacity-60" />
                                                No students currently enrolled with this academic badge.
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            );
                        })()}
                    </div>
                </Modal>
            )}


            {/* Add Subject to Class Sub-Modal */}
            {showSubjectForm && selectedClass && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowSubjectForm(false)}
                    title={editingSubject ? "Edit Academic Subject" : "Add Academic Subject"}
                    size="sm"
                    footer={
                        <>
                            <Button variant="secondary" type="button" onClick={() => setShowSubjectForm(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" form="subject-form">
                                {editingSubject ? "Save Changes" : "Add Subject"}
                            </Button>
                        </>
                    }
                >
                    <form id="subject-form" onSubmit={handleSubjectSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subject Name</label>
                            <input
                                type="text"
                                value={subjectForm.name}
                                onChange={(e) => setSubjectForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g. Mathematics"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white"
                                required
                            />
                        </div>
                        <div className="bg-emerald-50/50 p-3 rounded-xl border border-emerald-100/50 text-xs text-emerald-800 font-semibold">
                            Adding subject to <strong>Grade {selectedClass.grade} - Section {selectedClass.section}</strong>
                        </div>
                    </form>
                </Modal>
            )}
            {/* Schedule Timing Implementation Modal */}
            {showImplementationModal && selectedCampusForImplementation && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowImplementationModal(false)}
                    title="Schedule Timing Implementation"
                    size="sm"
                    footer={
                        <>
                            <Button 
                                variant="secondary" 
                                type="button" 
                                onClick={() => setShowImplementationModal(false)}
                                disabled={savingCampusTiming}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="button" 
                                loading={savingCampusTiming}
                                onClick={handleConfirmImplementation}
                            >
                                Confirm
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Please select when the updated day timing configuration for <strong>{selectedCampusForImplementation.name}</strong> should be implemented:
                        </p>
                        
                        <div className="space-y-3">
                            <label className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all ${
                                implementationTimeOption === 'immediate'
                                    ? 'border-emerald-500 bg-emerald-50/30'
                                    : 'border-gray-200 hover:bg-slate-50'
                            }`}>
                                <input
                                    type="radio"
                                    name="implementationTime"
                                    value="immediate"
                                    checked={implementationTimeOption === 'immediate'}
                                    onChange={() => setImplementationTimeOption('immediate')}
                                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                                />
                                <div className="ml-3">
                                    <span className="text-sm font-bold text-gray-900">Implement Immediately</span>
                                    <p className="text-xs text-gray-500 mt-0.5">Activate this timing change right away.</p>
                                </div>
                            </label>
                            
                            <label className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all ${
                                implementationTimeOption === 'next-monday'
                                    ? 'border-emerald-500 bg-emerald-50/30'
                                    : 'border-gray-200 hover:bg-slate-50'
                            }`}>
                                <input
                                    type="radio"
                                    name="implementationTime"
                                    value="next-monday"
                                    checked={implementationTimeOption === 'next-monday'}
                                    onChange={() => setImplementationTimeOption('next-monday')}
                                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                                />
                                <div className="ml-3">
                                    <span className="text-sm font-bold text-gray-900">Start of Next Week (Monday)</span>
                                    <p className="text-xs text-gray-500 mt-0.5">Will become effective on {getNextMondayStr()}</p>
                                </div>
                            </label>
                            
                            <label className={`flex flex-col p-3 border rounded-xl cursor-pointer transition-all ${
                                implementationTimeOption === 'custom'
                                    ? 'border-emerald-500 bg-emerald-50/30'
                                    : 'border-gray-200 hover:bg-slate-50'
                            }`}>
                                <div className="flex items-center">
                                    <input
                                        type="radio"
                                        name="implementationTime"
                                        value="custom"
                                        checked={implementationTimeOption === 'custom'}
                                        onChange={() => setImplementationTimeOption('custom')}
                                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                                    />
                                    <div className="ml-3">
                                        <span className="text-sm font-bold text-gray-900">Select Custom Implementation Date</span>
                                        <p className="text-xs text-gray-500 mt-0.5">Choose a specific date for schedule activation.</p>
                                    </div>
                                </div>
                                
                                {implementationTimeOption === 'custom' && (
                                    <div className="mt-3 pl-7">
                                        <input
                                            type="date"
                                            value={customImplementationDate}
                                            onChange={(e) => setCustomImplementationDate(e.target.value)}
                                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            required
                                        />
                                    </div>
                                )}
                            </label>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Custom Delete Confirmation Modal */}
            {deleteTarget && (
                <Modal
                    isOpen={true}
                    onClose={() => setDeleteTarget(null)}
                    title={deleteTarget.title}
                    size="sm"
                    footer={
                        <>
                            <Button
                                variant="secondary"
                                onClick={() => setDeleteTarget(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleConfirmDelete}
                            >
                                Delete
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-3 p-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Warning Action</p>
                        <p className="text-sm font-semibold text-gray-700 leading-relaxed">
                            {deleteTarget.message}
                        </p>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AdminManagement;